import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { useState } from 'react';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { ok, err } from 'neverthrow';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@cotera/client/v0/components/ui/toggle-group';
import { Globe, Lock } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../navigation';

type Params = {
  name: string;
  parentId?: string | null;
  scope?: 'user' | 'org';
};

type ResolvedParams = {
  name: string;
  parentId: string | null;
  scope: 'user' | 'org';
};

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type CreateFolderActionClient = {
  resource: Pick<TenantedClient['resource'], 'get'> & {
    folder: Pick<TenantedClient['resource']['folder'], 'create'>;
  };
};

function getParentFolderIdFromContext(
  context: ApplicableContext
): string | null {
  const folderPageModel = context.getInScopeModelOfType(
    FolderContentsPageModel
  );
  if (folderPageModel === null || folderPageModel.folderId === null) {
    return null;
  }
  return folderPageModel.folderId;
}

export class CreateFolderAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Create folder';
  description = 'Create a new folder for organizing chats and agents';
  inputSchema = z.object({
    name: z.string().min(1),
    parentId: z.string().uuid().nullable().optional(),
    scope: z.enum(['user', 'org']).optional(),
  });
  group = 'app';
  icon = 'folder';

  private _lastCreatedFolderId: string | null = null;
  private _lastParentId: string | null = null;

  constructor(
    private readonly client: CreateFolderActionClient,
    private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>,
    private readonly orgId: string,
    private readonly modalManager: ModalManager,
    private readonly fallbackNavigator: ResourceNavigator
  ) {
    super();
  }

  private async resolveScopeForParentFolder(
    parentId: string
  ): Promise<'user' | 'org' | null> {
    const parentFolder = await this.client.resource.get({
      type: ResourceType.Folder,
      id: parentId,
    });

    if (
      parentFolder.isErr() ||
      parentFolder.value.type !== ResourceType.Folder
    ) {
      return null;
    }

    return parentFolder.value.folder.scope === 'user' ? 'user' : 'org';
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const parentId = payload.parentId ?? null;
    let scope = payload.scope;

    if (scope === undefined) {
      if (parentId === null) {
        const msg = 'Scope is required when creating a top-level folder';
        toast.error(msg);
        return err([{ t: 'error', message: msg }]);
      }
      const inherited = await this.resolveScopeForParentFolder(parentId);
      if (inherited === null) {
        const msg = 'Failed to inherit parent folder permissions';
        toast.error(msg);
        return err([{ t: 'error', message: msg }]);
      }
      scope = inherited;
    }

    const result = await this.client.resource.folder.create({
      name: payload.name.trim(),
      parentId,
      scope,
    });

    if (result.isErr()) {
      const msg =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to create folder';
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    const createdFolderId = result.value.id;
    this._lastCreatedFolderId = createdFolderId;
    this._lastParentId = parentId;

    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.resource.folderTree({ orgId: this.orgId }),
    });

    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Folder,
      resourceId: createdFolderId,
      name: payload.name.trim(),
    });

    toast.success(parentId === null ? 'Folder created' : 'Subfolder created');
    return ok({});
  }

  track({
    payload,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    if (this._lastCreatedFolderId === null) {
      return null;
    }
    const parentId = this._lastParentId;
    return {
      event: CocoEvent.FolderCreated,
      properties: {
        folderId: this._lastCreatedFolderId,
        hasParent: parentId !== null,
        parentFolderId: parentId,
        scope: payload.scope ?? null,
      },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const parentIdFromContext = getParentFolderIdFromContext(context);
    const parentId =
      params.parentId !== undefined ? params.parentId : parentIdFromContext;

    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <CreateFolderAskView
              initialParams={{ ...params, parentId }}
              resolveScopeForParentFolder={async (resolvedParentId) =>
                await this.resolveScopeForParentFolder(resolvedParentId)
              }
              onRespond={async (p) => {
                await onRespond(p);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }
}

const CreateFolderAskView: React.FC<{
  initialParams?: Partial<Params> & { parentId?: string | null };
  resolveScopeForParentFolder: (
    parentId: string
  ) => Promise<'user' | 'org' | null>;
  onRespond: (params: ResolvedParams) => Promise<void>;
  onCancel: () => void;
}> = ({ initialParams, resolveScopeForParentFolder, onRespond, onCancel }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const parentId = initialParams?.parentId ?? null;
  const isSubfolder = parentId !== null;
  // A subfolder inherits its parent's scope, and a caller that already knows the
  // scope passes it in. Only a top-level folder with no scope supplied needs the
  // user to pick — that's the one case this toggle covers.
  const needsScopeChoice = !isSubfolder && initialParams?.scope === undefined;
  const [selectedScope, setSelectedScope] = useState<'user' | 'org'>('user');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return;
    }
    setLoading(true);
    let scope = initialParams?.scope;
    if (scope === undefined) {
      if (parentId === null) {
        scope = selectedScope;
      } else {
        const inherited = await resolveScopeForParentFolder(parentId);
        if (inherited === null) {
          toast.error('Failed to inherit parent folder permissions');
          setLoading(false);
          return;
        }
        scope = inherited;
      }
    }
    await onRespond({
      name: trimmed,
      parentId,
      scope,
    });
    setLoading(false);
  };

  return (
    <ModalPageView
      title={isSubfolder ? 'Create subfolder' : 'Create folder'}
      subtitle={
        isSubfolder
          ? 'Choose a name for the new subfolder'
          : 'Choose a name for your new folder'
      }
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="folder-name" className="text-sm font-medium">
            Folder name
          </label>
          <Input
            id="folder-name"
            autoFocus
            placeholder="e.g. Research, Projects"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {needsScopeChoice && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Visibility</label>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={selectedScope}
              onValueChange={(value) => {
                // Radix emits '' when the active item is re-clicked; ignore it
                // so a scope is always selected.
                if (value === 'user' || value === 'org') {
                  setSelectedScope(value);
                }
              }}
              aria-label="Folder visibility"
            >
              <ToggleGroupItem value="user" aria-label="Private">
                <Lock className="h-4 w-4 mr-1.5" />
                Private
              </ToggleGroupItem>
              <ToggleGroupItem value="org" aria-label="Shared">
                <Globe className="h-4 w-4 mr-1.5" />
                Shared
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || name.trim().length === 0}
            variant="default"
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Create'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
